import { isDefined, isNotDefined } from '@sgme/fp';

import type {
  EspExternalUserLadder,
  EspInternalUserLadder,
  EspState,
  EspStreamClientPriced,
  EspStreamPriced,
  EspStreamTraderPriced,
} from '@/store/esp/state.ts';
import { convertPointsToRate } from '@/utils/number-point.ts';

export const initEspState = (): EspState => ({
  streams: {},
  limits: {},
});

export const getEspStreamId = (currencyPair: string, tiering: string) => `${currencyPair}&&${tiering}`;

export const isEspStreamTraderPriced = (price: EspStreamPriced): price is EspStreamTraderPriced =>
  price.priceType === 'ESP.TRADER.PRICE';

export const isEspStreamClientPriced = (price: EspStreamPriced): price is EspStreamClientPriced =>
  price.priceType === 'ESP.CLIENT.PRICE';

export const isEspInternalUserLadder = (
  ladder: EspInternalUserLadder | EspExternalUserLadder,
): ladder is EspInternalUserLadder => isDefined((ladder as EspInternalUserLadder).traderBid);

export const isEspExternalUserLadder = (
  ladder: EspInternalUserLadder | EspExternalUserLadder,
): ladder is EspExternalUserLadder => isDefined((ladder as EspExternalUserLadder).clientBid);

export const getPriceMargin = (
  espPrice: EspStreamPriced,
  notional: number | null | undefined,
  notionalCurrencyIndex: 1 | 2,
) => {
  return espPrice.priceType === 'ESP.TRADER.PRICE'
    ? getInternalUserPriceMargin(espPrice, notional, notionalCurrencyIndex)
    : getExternalUserPriceMargin(espPrice, notional, notionalCurrencyIndex);
};

export const getInternalUserPriceMargin = (
  espPrice: EspStreamTraderPriced,
  notional: number | null | undefined,
  notionalCurrencyIndex: 1 | 2,
) => {
  const precision = espPrice.currencyPairPrecision;
  const ladder = getLadderForNotional(espPrice, notional ?? 0, notionalCurrencyIndex);

  if (isNotDefined(ladder)) {
    return { withMargin: { bid: 0, ask: 0 }, withoutMargin: { bid: 0, ask: 0 }, spread: 0, precision };
  }

  return {
    withMargin: {
      bid: convertPointsToRate(espPrice.midRate + ladder.traderBid + ladder.marginBid, precision),
      ask: convertPointsToRate(espPrice.midRate + ladder.traderAsk + ladder.marginAsk, precision),
    },
    withoutMargin: {
      bid: convertPointsToRate(espPrice.midRate + ladder.traderBid, precision),
      ask: convertPointsToRate(espPrice.midRate + ladder.traderAsk, precision),
    },
    spread: (ladder.marginBid - ladder.marginAsk) / 10, // not in points, but in pips
    precision,
  };
};

export const getExternalUserPriceMargin = (
  espPrice: EspStreamClientPriced,
  notional: number | null | undefined,
  notionalCurrencyIndex: 1 | 2,
) => {
  const precision = espPrice.currencyPairPrecision;
  const ladder = getLadderForNotional(espPrice, notional ?? 0, notionalCurrencyIndex);

  if (isNotDefined(ladder)) {
    return { withMargin: { bid: 0, ask: 0 }, withoutMargin: { bid: 0, ask: 0 }, spread: 0, precision };
  }

  return {
    withMargin: {
      bid: convertPointsToRate(espPrice.midRate + ladder.clientBid, precision),
      ask: convertPointsToRate(espPrice.midRate + ladder.clientAsk, precision),
    },
    withoutMargin: {
      bid: convertPointsToRate(espPrice.midRate + ladder.clientBid, precision),
      ask: convertPointsToRate(espPrice.midRate + ladder.clientAsk, precision),
    },
    spread: (ladder.clientBid - ladder.clientAsk) / 10, // not in points, but in pips
    precision,
  };
};

export type EspStreamPrice<Price extends EspStreamPriced> = Price extends EspStreamTraderPriced
  ? EspInternalUserLadder
  : Price extends EspStreamClientPriced
    ? EspExternalUserLadder
    : never;

/**
 * Get the ladder that matches the provided notional amount and notional currency index.
 */
export const getLadderForNotional = <Price extends EspStreamPriced>(
  espPrice: Price,
  notional: number | undefined,
  notionalCurrencyIndex: 1 | 2,
): EspStreamPrice<Price> | undefined => {
  const ladderIndex = getLadderIndexForNotional(espPrice, notional, notionalCurrencyIndex);

  return ladderIndex >= 0 ? (espPrice.ladders?.[ladderIndex] as EspStreamPrice<Price>) : undefined;
};

export const getLadderIndexForNotional = <Price extends EspStreamPriced>(
  espPrice: Price,
  notional: number | undefined,
  notionalCurrencyIndex: 1 | 2,
): number => {
  if (isNotDefined(notional) || notional === 0) {
    return isDefined(espPrice.ladders) && espPrice.ladders.length > 0 ? 0 : -1;
  }

  // trader prices
  if (isEspStreamTraderPriced(espPrice)) {
    return espPrice.ladders?.findIndex((ladder) => {
      const thresholdBidScale =
        notionalCurrencyIndex === 1
          ? 1
          : convertPointsToRate(espPrice.midRate + ladder.traderBid, espPrice.currencyPairPrecision);

      const thresholdAskScale =
        notionalCurrencyIndex === 1
          ? 1
          : convertPointsToRate(espPrice.midRate + ladder.traderAsk, espPrice.currencyPairPrecision);

      return notional <= thresholdBidScale * ladder.thresholdBid && notional <= thresholdAskScale * ladder.thresholdAsk;
    });
  }

  // client prices
  return espPrice.ladders?.findIndex((ladder) => {
    const thresholdBidScale =
      notionalCurrencyIndex === 1
        ? 1
        : convertPointsToRate(espPrice.midRate + ladder.clientBid, espPrice.currencyPairPrecision);
    const thresholdAskScale =
      notionalCurrencyIndex === 1
        ? 1
        : convertPointsToRate(espPrice.midRate + ladder.clientAsk, espPrice.currencyPairPrecision);

    return notional <= thresholdBidScale * ladder.thresholdBid && notional <= thresholdAskScale * ladder.thresholdAsk;
  });
};

// ███████╗██╗   ██╗██████╗ ███████╗ ██████╗██████╗ ██╗██████╗ ███████╗    ███████╗███████╗██████╗
// ██╔════╝██║   ██║██╔══██╗██╔════╝██╔════╝██╔══██╗██║██╔══██╗██╔════╝    ██╔════╝██╔════╝██╔══██╗
// ███████╗██║   ██║██████╔╝███████╗██║     ██████╔╝██║██████╔╝█████╗      █████╗  ███████╗██████╔╝
// ╚════██║██║   ██║██╔══██╗╚════██║██║     ██╔══██╗██║██╔══██╗██╔══╝      ██╔══╝  ╚════██║██╔═══╝
// ███████║╚██████╔╝██████╔╝███████║╚██████╗██║  ██║██║██████╔╝███████╗    ███████╗███████║██║
// ╚══════╝ ╚═════╝ ╚═════╝ ╚══════╝ ╚═════╝╚═╝  ╚═╝╚═╝╚═════╝ ╚══════╝    ╚══════╝╚══════╝╚═╝
// TODO: add company ID ?

export const getRequestMutationCacheKey = (currencyPair: string, tiering: string) =>
  `esp-request-${currencyPair}-${tiering}`;
